/**
 * Title - Pure presentational component for rendering labels and values
 *
 * Handles layout/positioning of label + value combinations:
 * - 'separated': Label on separate line above value (default)
 * - 'inline': Label inline with value (e.g., "Step: 5")
 * - 'value': Only value displayed (e.g., for row headers in grid mode)
 * - 'hidden': Renders nothing
 *
 * The parent is responsible for determining what label and value to display.
 * Value can be a React node (e.g., for run components with links).
 */

import React from 'react';
import {twMerge} from 'tailwind-merge';

import {type TitleProps} from './types';

export type {TitleProps};

const getTitleClassNames = (className?: string) => {
  return twMerge('text__single-line text-wb-text-tertiary', className);
};

/**
 * Renders a title with label and value.
 *
 * @param props - TitleProps
 * @returns React element
 */
export const Title: React.FC<TitleProps> = ({
  label,
  value,
  valueClassName,
  valueStyle,
  className,
  labelPosition = 'inline',
  showColon = false,
  'data-test': dataTest = 'title',
}) => {
  if (labelPosition === 'hidden') {
    return null;
  }

  const effectiveValueClassName = valueClassName || getTitleClassNames();

  if (labelPosition === 'value') {
    return (
      <div
        className={twMerge('flex flex-1 min-w-0', className)}
        data-test={dataTest}>
        <div
          className={twMerge(effectiveValueClassName, 'truncate min-h-[20px]')}
          style={valueStyle}>
          {value}
        </div>
      </div>
    );
  }

  if (labelPosition === 'inline') {
    return (
      <div className="flex flex-row gap-1 min-w-0 max-w-full h-full">
        <span className="text-center truncate text-wb-text-tertiary h-full">
          {label}
          {showColon ? ':' : ' '}
          {value}
        </span>
      </div>
    );
  }

  return (
    <div
      className={twMerge('flex flex-col flex-1 min-w-0', className)}
      data-test={dataTest}>
      <div className="flex flex-col gap-0 min-w-0">
        <div className="text-start text-xs/5 text-wb-text-tertiary truncate">
          {label}
        </div>
        <div className="flex items-center min-w-0">
          <div
            className={twMerge(
              valueClassName,
              'text-md leading-6 font-semibold min-w-0 truncate'
            )}
            style={valueStyle}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
};
